import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

/**
 * Upload a file to Firebase Storage with progress tracking
 * @param {File} file - The file to upload
 * @param {string} userId - The user ID for organizing files
 * @param {Function} onProgress - Callback for upload progress (0-100)
 * @returns {Promise<{success: boolean, url?: string, path?: string, error?: string}>}
 */
export const uploadDocument = (file, userId, onProgress) => {
  return new Promise((resolve, reject) => {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }

      // Validate file type
      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }

      // Validate file size (50MB max)
      const maxSize = 50 * 1024 * 1024; // 50MB in bytes
      if (file.size > maxSize) {
        throw new Error('File size must be less than 50MB');
      }

      // Create unique filename
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${timestamp}_${sanitizedName}`;
      const storagePath = `documents/${userId}/${fileName}`;

      // Create storage reference
      const storageRef = ref(storage, storagePath);

      // Start upload
      const uploadTask = uploadBytesResumable(storageRef, file);

      // Monitor upload progress
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Calculate progress percentage
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(Math.round(progress));
          }
        },
        (error) => {
          // Handle upload errors
          console.error('Upload error:', error);
          reject({
            success: false,
            error: error.message || 'Upload failed',
          });
        },
        async () => {
          // Upload completed successfully
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({
              success: true,
              url: downloadURL,
              path: storagePath,
              fileName: file.name,
              fileSize: file.size,
            });
          } catch (error) {
            reject({
              success: false,
              error: 'Failed to get download URL',
            });
          }
        }
      );
    } catch (error) {
      reject({
        success: false,
        error: error.message || 'Upload failed',
      });
    }
  });
};

/**
 * Delete a file from Firebase Storage
 * @param {string} storagePath - The path to the file in storage
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteDocument = async (storagePath) => {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    return { success: true };
  } catch (error) {
    console.error('Delete error:', error);
    return {
      success: false,
      error: error.message || 'Delete failed',
    };
  }
};

/**
 * Get download URL for a file
 * @param {string} storagePath - The path to the file in storage
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const getDocumentURL = async (storagePath) => {
  try {
    const storageRef = ref(storage, storagePath);
    const url = await getDownloadURL(storageRef);
    return { success: true, url };
  } catch (error) {
    console.error('Get URL error:', error);
    return {
      success: false,
      error: error.message || 'Failed to get download URL',
    };
  }
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};
